import { isAcTerminalId } from "./ac";
import type { ComponentDefinition, Terminal } from "./types";

/** PV and AC terminal prefixes are schema conventions, not component IDs. */
export function isPvTerminalId(id: string): boolean {
  return /^pv(?:[-_]|$)/i.test(id);
}

/** Direct DC generation is a component capability, independent of catalog ID.
 * AC inlets need a charger before they can supply a DC battery circuit. */
export function isDirectDcSource(def: ComponentDefinition): boolean {
  return def.category === "engine" || (def.category === "source" &&
    def.terminals.some(t => !isAcTerminalId(t.id) && t.polarity === "positive"));
}

/** Exact catalog source with a regulated power ceiling, rather than the
 * alternator operating estimate. Its documented watts and current limit remain
 * on the catalog definition. Wind/hydro output is not inferred from a converter
 * rating or from this fuel-cell operating point. */
export function isRegulatedDcSource(def: ComponentDefinition): boolean {
  return def.id === "src-efoy-80";
}

/** An inverter/charger uses the same physical DC pair in both directions.
 * Older catalogue entries and imported drawings label that pair "input".
 * Capability detection keeps those drawings working without inventing a second
 * DC port or connecting the AC rails to the battery. */
export function isInverterCharger(def: ComponentDefinition): boolean {
  return def.category === "charger" && (def.ampDraw ?? 0) > 0 && (def.ampOutput ?? 0) > 0 &&
    def.terminals.some(t => isAcTerminalId(t.id) && t.direction === "input") &&
    def.terminals.some(t => isAcTerminalId(t.id) && t.direction === "output");
}

/** An inverter draws from DC to supply AC. Legacy load definitions may
 * declare only their DC terminals and identify the inverter in the model ID. */
export function isBatteryPoweredInverter(def: ComponentDefinition): boolean {
  if (isInverterCharger(def)) return true;
  if (def.category !== "load") return false;
  const ac = def.terminals.filter(t => isAcTerminalId(t.id));
  return ac.length ? ac.some(t => t.direction === "output" && /[-_]l[12]?$/i.test(t.id))
    : def.id.includes("inverter");
}

/** Select a declared DC side without accidentally pairing a vehicle input
 * with a separate PV input. A single shared negative may be labelled output
 * on non-isolated converters; an explicitly declared input return always
 * wins, even when that return is unwired in the drawing.
 *
 * This selects terminals only. It never adds conductive links across a
 * converter, and never merges independently declared output terminals. */
export function dcSideTerminals(
  def: ComponentDefinition,
  side: "input" | "output" | "bidi",
): Terminal[] {
  const candidates = def.terminals.filter(t => !isAcTerminalId(t.id) &&
    (t.polarity === "positive" || t.polarity === "negative" || t.polarity === "ground"));
  // A declared DC negative is the current return. Additional ground terminals
  // are protective chassis bonds, not another supply port or a substitute for
  // a missing negative wire. Ground-only devices (for example engine blocks)
  // retain their declared common return.
  const hasNegative = candidates.some(t => t.polarity === "negative");
  const dc = candidates.filter(t => !hasNegative || t.polarity !== "ground");
  if (isInverterCharger(def)) return dc.filter(t => !isPvTerminalId(t.id));
  const separateDcInput = side === "input" && def.category === "charger" && dc.some(t =>
    t.direction === "input" && t.polarity === "positive" && !isPvTerminalId(t.id));
  const selected = dc.filter(t =>
    !(separateDcInput && t.direction === "input" && isPvTerminalId(t.id)) &&
    (t.direction === side || (t.direction === "bidi" && side !== "bidi")));
  if (separateDcInput && !selected.some(t => t.polarity !== "positive")) {
    const common = dc.filter(t => t.direction === "output" && t.polarity !== "positive" && !isPvTerminalId(t.id));
    if (common.length === 1) selected.push(common[0]);
  }
  return selected;
}

/** Independent DC ports must not borrow each other's returns. Paired terminal
 * suffixes (batt-pos/batt-neg, load-pos/load-neg, etc.) identify separate
 * ports; bare pos/neg IDs identify the unnamed port. With a single declared
 * negative it is shared; ambiguous unpaired positives remain incomplete
 * instead of fabricating a return. */
export function dcPortTerminalGroups(def: ComponentDefinition, side: "input" | "output" | "bidi"): Terminal[][] {
  const selected = dcSideTerminals(def, side);
  const positives = selected.filter(t => t.polarity === "positive");
  const negatives = selected.filter(t => t.polarity !== "positive");
  const stem = (id: string): string | undefined => {
    const match = /^(?:(.*)[-_])?(?:pos|neg|positive|negative|plus|minus)$/i.exec(id);
    return match ? (match[1] ?? "").toLowerCase() : undefined;
  };
  return positives.map(p => {
    if (negatives.length <= 1) return [p, ...negatives];
    const name = stem(p.id);
    const paired = name == null ? [] : negatives.filter(n => stem(n.id) === name);
    return [p, ...paired];
  });
}

/** Charging ports include the supported legacy engine with one combined
 * starter/alternator stud labelled input. A dedicated output always wins, so
 * an unwired alternator cannot borrow a connected starter's positive cable.
 * This is the same generation-side rule used by the signed current model. */
export function dcChargingPortGroups(def: ComponentDefinition): Terminal[][] {
  const hasDedicatedOutput = def.terminals.some(t => t.direction === "output");
  const side = def.category === "engine" && !hasDedicatedOutput ? "input" : "output";
  // Generic output selection also accepts bidi positive studs. An engine with
  // any dedicated output uses every other positive stud only for starting;
  // its shared bidi negative/ground return must remain available to the output.
  const chargingDef = def.category === "engine" && hasDedicatedOutput
    ? { ...def, terminals: def.terminals.filter(t => t.polarity !== "positive" || t.direction === "output") }
    : def;
  return dcPortTerminalGroups(chargingDef, side);
}

/** Physical PV input pairs. A single declared bidirectional DC negative may
 * serve PV, vehicle and battery ports; an independent return or chassis earth
 * must never be borrowed to complete a missing PV negative. */
export function pvInputTerminalGroups(def: ComponentDefinition): Terminal[][] {
  const pv = def.terminals.filter(t => t.direction === "input" && isPvTerminalId(t.id));
  const declaredReturn = pv.some(t => t.polarity === "negative");
  const dcNegatives = def.terminals.filter(t => !isAcTerminalId(t.id) && t.polarity === "negative");
  const shared = !declaredReturn && dcNegatives.length === 1 && dcNegatives[0].direction === "bidi"
    ? dcNegatives : [];
  return dcPortTerminalGroups({ ...def, terminals: [...pv, ...shared] }, "input");
}

/** Explicit passive bonds are physical copper, including when conversion is off.
 * Validate imported metadata before it can create conductive graph edges. */
export function internalDcConnections(def: ComponentDefinition): Array<readonly [string, string]> {
  const declared: unknown = def.internalDcConnections;
  if (!Array.isArray(declared)) return [];
  const valid: Array<readonly [string, string]> = [];
  for (const pair of declared) {
    if (!Array.isArray(pair) || pair.length !== 2 ||
        !pair.every(id => typeof id === "string")) continue;
    const [a, b] = pair as [string, string];
    const left = def.terminals.find(t => t.id === a);
    const right = def.terminals.find(t => t.id === b);
    if (a === b || !left || !right || isAcTerminalId(a) || isAcTerminalId(b) ||
        (left.polarity !== "positive" && left.polarity !== "negative") ||
        left.polarity !== right.polarity) continue;
    // A shared return or duplicate stud is copper; an input-to-output
    // positive connection would bypass the converter's power stage.
    if (left.polarity === "positive" && (def.category === "charger" || def.category === "load") &&
        left.direction !== right.direction) continue;
    valid.push([a, b]);
  }
  return valid;
}


/** Declared one-way isolator paths. They must never enter an undirected
 * conductor graph; input/output ratings do not establish operating current. */
export function oneWayDcConnections(def: ComponentDefinition): Array<readonly [string, string]> {
  const declared: unknown = def.oneWayDcConnections;
  if (def.category !== "distribution" || !Array.isArray(declared)) return [];
  const valid: Array<readonly [string, string]> = [];
  for (const pair of declared) {
    if (!Array.isArray(pair) || pair.length !== 2 || !pair.every(id => typeof id === "string")) continue;
    const [from, to] = pair as [string, string];
    const input = def.terminals.find(t => t.id === from);
    const output = def.terminals.find(t => t.id === to);
    if (from === to || !input || !output || isAcTerminalId(from) || isAcTerminalId(to) ||
        input.polarity !== "positive" || output.polarity !== "positive" ||
        input.direction !== "input" || output.direction !== "output") continue;
    valid.push([from, to]);
  }
  return valid;
}
